import ChevronIcon from "@/assets/colorable/chevron.svg?react";
import TickIcon from "@/assets/colorable/tick.svg?react";
import InputField from "@/components/Common/forms/InputField";
import ReusableDropdown, {type TDropdownOption} from "@/components/Common/forms/ReusableDropdown";
import clsxm from "@/lib/clsxm";
import InfiniteLimitSetter from "@/pages/plans/InfiniteLimitSetter";
import {TIKTOK_FREQUENCY_BY_HOURS, TIKTOK_KEYWORD_LIMIT_INFINITY} from "@/utils/planUtils";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const TikTokVisibilityLimitFields: FC<{
  countryCount: number | null | undefined;
  isDisabled?: boolean;
  keywordLimit: number | null | undefined;
  onCountryCountChange: (value: number) => void;
  onKeywordLimitChange: (value: number) => void;
  onSyncIntervalChange: (value: number) => void;
  syncInterval: number | null | undefined;
}> = ({
  countryCount,
  isDisabled,
  keywordLimit,
  onCountryCountChange,
  onKeywordLimitChange,
  onSyncIntervalChange,
  syncInterval,
}) => {
  const {t} = useTranslation("common");
  const syncIntervalOptions: TDropdownOption[] = Object.entries(TIKTOK_FREQUENCY_BY_HOURS).map(
    ([hours, {sentence}]) => ({
      value: hours,
      label: t(sentence as "tiktok_visibility_sync_interval_hours_daily"),
    }),
  );

  return (
    <>
      <InputField
        label={t("tiktok_keyword_limit")}
        type="number"
        className="h-10"
        isDisabled={isDisabled}
        labelClassName="text-neutral-black mb-0 text-xs font-normal"
        containerClassName="justify-between lg:flex-1"
        placeholder="#"
        onChange={value => {
          if (+value >= 0) onKeywordLimitChange(+value);
        }}
        value={
          typeof keywordLimit !== "number" || keywordLimit >= TIKTOK_KEYWORD_LIMIT_INFINITY
            ? ""
            : keywordLimit.toString()
        }
        icon={
          <InfiniteLimitSetter
            infiniteValue={TIKTOK_KEYWORD_LIMIT_INFINITY}
            onChange={onKeywordLimitChange}
            value={keywordLimit}
          />
        }
      />
      <ReusableDropdown
        label={t("tiktok_sync_interval_hours")}
        isDisabled={isDisabled}
        containerClassName="lg:flex-1"
        setSelectedOption={option => onSyncIntervalChange(+option.value)}
        selectedOption={syncIntervalOptions.find(option => +option.value === syncInterval)}
        options={syncIntervalOptions}
        renderButton={({selectedOption, isOpen, className, ...rest}) => (
          <button
            {...rest}
            className={clsxm(
              className,
              isOpen ? "border-primary-medium-solid" : "border-neutral-grey-light",
              selectedOption ? "text-neutral-black font-medium" : "text-neutral-grey-light",
              "h-10 gap-2 rounded-md pl-4",
            )}>
            <span className="grow items-center text-left">
              {selectedOption?.label ?? t("tiktok_sync_interval_hours")}
            </span>
            <ChevronIcon
              className={clsxm(
                "text-primary-dark-solid mr-4 transition-transform duration-250",
                isOpen ? "rotate-180" : "rotate-0",
              )}
            />
          </button>
        )}
        renderItem={({option, isHighlighted, isSelected, className, ...rest}) => (
          <li
            {...rest}
            value={option.value}
            className={clsxm(
              className,
              isHighlighted ? "bg-primary-light-solid" : "hover:bg-neutral-white-dark",
              "h-10 gap-2",
            )}>
            <span className="grow leading-none">{option.label}</span>
            {isSelected && <TickIcon className="text-primary-dark-solid" />}
          </li>
        )}
        labelClassName="text-neutral-black text-xs font-normal"
      />
      <InputField
        label={t("tiktok_country_limit")}
        type="number"
        className="h-10"
        isDisabled={isDisabled}
        labelClassName="text-neutral-black mb-0 text-xs font-normal"
        containerClassName="justify-between lg:flex-1"
        placeholder="#"
        onChange={value => {
          if (+value >= 0) onCountryCountChange(+value);
        }}
        value={typeof countryCount !== "number" ? "" : countryCount.toString()}
      />
    </>
  );
};

export default TikTokVisibilityLimitFields;
